import { useEffect } from 'react';
import {
  mapOgRoomToNodeV2,
  type NodeRoomV2,
  useGetRoomsForUserQuery,
} from '../../services/room';
import { useGetRoomsQuery } from '../../services/users';
import { getLogger } from '../../libs/logger';
import { getIsGraphqlRoomQueriesEnabled } from '../../utils/isGraphqlRoomQueriesEnabled';

export { type NodeRoomV2 };

const logger = getLogger('useRooms');

type UseRoomsProps = {
  userId: string;
  institutionId: number | null;
  skip?: boolean;
};

type UseRoomsReturnType = {
  rooms: NodeRoomV2[] | undefined;
  isLoading: boolean;
  isError: boolean;
};

/**
 * Get the rooms of a provider.
 */
export const useRooms = ({
  userId,
  institutionId,
  skip = false,
}: UseRoomsProps): UseRoomsReturnType => {
  const isGraphqlRoomQueriesEnabled = getIsGraphqlRoomQueriesEnabled();

  const {
    currentData: ogRooms,
    isLoading: loadingRtkQuery,
    error: errorRtkQuery,
  } = useGetRoomsQuery(
    { userId, institutionId },
    { skip: skip || isGraphqlRoomQueriesEnabled },
  );

  const {
    currentData: gqlData,
    isLoading: loadingGraphqlQuery,
    error: errorGraphqlQuery,
  } = useGetRoomsForUserQuery(undefined, {
    skip: skip || !isGraphqlRoomQueriesEnabled,
  });

  const { rooms, error, isLoading } = isGraphqlRoomQueriesEnabled
    ? {
        rooms: gqlData?.Room?.getRoomsForUser?.edges || [],
        error: errorGraphqlQuery,
        isLoading: loadingGraphqlQuery,
      }
    : {
        rooms: ogRooms?.map(mapOgRoomToNodeV2) || [],
        error: errorRtkQuery,
        isLoading: loadingRtkQuery,
      };

  useEffect(() => {
    if (error) {
      logger.error('Failed to get rooms');
    }
  }, [error]);

  return {
    rooms,
    isLoading,
    isError: error != null,
  };
};
